import { useCallback, useEffect, useRef, useState } from 'react'
import type { Audience, BotItem, LeadState, Message, StepId } from '../types'
import { STEP_ORDER } from '../config'
import { stepDefinitions } from '../steps/stepDefinitions'

const STORAGE_KEY = 'leadflow_v4_state'

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

interface CompleteResult { ok: boolean; error?: string }

interface RunnerOptions {
  audience: Audience
  initialState?: LeadState
  initialStep?: StepId
  onStepView?: (step: StepId) => void
  onStepSubmit?: (step: StepId, state: LeadState) => void
  /**
   * Returnerer {ok: false} hvis backend ikke accepterede leadet (4xx, netværks-
   * fejl, etc.) så runneren kan vise fejl-besked i stedet for thank-you. Hvis
   * implementationen ikke returnerer noget tolkes det som success (bagudkomp.
   * for tests/mocks der ikke kan returnere).
   */
  onComplete?: (state: LeadState) => Promise<CompleteResult | void> | CompleteResult | void
}

interface RunnerReturn {
  state: LeadState
  currentStep: StepId
  messages: Message[]
  inputVisible: boolean
  isPosting: boolean
  submitStep: (updates: Partial<LeadState>) => Promise<void>
  reset: () => void
  resumeFrom: (step: StepId, state: LeadState) => void
}

function nextStepId(current: StepId, state: LeadState, audience: Audience): StepId | null {
  const order = STEP_ORDER[audience]
  const idx = order.indexOf(current)
  let next: StepId | undefined = order[idx + 1]
  // Branch: "Ingen forsikring endnu" → skip straight to phone
  const hasNone = (state.insurances ?? []).includes('ingen')
  if (hasNone && current === 'insurances') next = 'phone'
  return next ?? null
}

export function useChatRunner({ audience, initialState, initialStep, onStepView, onStepSubmit, onComplete }: RunnerOptions): RunnerReturn {
  // initialState er Widget'ens kontrol: den passeres KUN når useResume har
  // bekræftet at brugeren valgte "Fortsæt". Ved "Start forfra" eller første
  // gang er initialState undefined — vi skal IKKE falde tilbage til
  // readPersistedState() her, fordi det ville lække den gamle (måske afsluttede)
  // sessions state ind i et nyt flow. Widget'en er ansvarlig for at rydde
  // localStorage før re-mount når brugeren valgte restart.
  const [state, setState] = useState<LeadState>(() => initialState ?? {})
  const [currentStep, setCurrentStep] = useState<StepId>(initialStep ?? 'welcome')
  const [messages, setMessages] = useState<Message[]>([])
  const [inputVisible, setInputVisible] = useState(false)
  const [isPosting, setIsPosting] = useState(false)

  const messageKeyRef = useRef(0)
  const initRef = useRef(false)

  // Persist
  useEffect(() => {
    try {
      const toSave = { ...state, _step: currentStep }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
    } catch { /* quota or disabled */ }
  }, [state, currentStep])

  const postBotMessages = useCallback(async (items: BotItem[]) => {
    setIsPosting(true)
    setInputVisible(false)
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      // CTA er en handlings-knap — den "skrives" ikke af bot'en, så vi springer
      // typing-animationen over og lader knappen bare poppe ind efter sidste
      // besked. Tekst og savings-card får typing-dots som sædvanligt.
      const isCta = typeof item !== 'string' && item.kind === 'cta'

      let typingKey: string | null = null
      if (!isCta) {
        typingKey = `typing-${messageKeyRef.current++}`
        setMessages((prev) => [...prev, { type: 'typing', key: typingKey! }])
        // Rich items (savings card) get a slightly longer typing pause for drama
        const typingDuration = typeof item === 'string'
          ? Math.min(1200, Math.max(600, item.length * 20))
          : 1100
        await sleep(typingDuration)
      }

      const msgKey = `msg-${messageKeyRef.current++}`
      let newMsg: Message
      if (typeof item === 'string') {
        newMsg = { type: 'bot', text: item, key: msgKey }
      } else if (item.kind === 'savings') {
        newMsg = { type: 'savings', min: item.min, max: item.max, key: msgKey }
      } else {
        newMsg = { type: 'cta', label: item.label, key: msgKey }
      }
      setMessages((prev) => prev.filter((m) => m.key !== typingKey).concat(newMsg))
      if (i < items.length - 1) await sleep(400)
    }
    await sleep(300)
    setInputVisible(true)
    setIsPosting(false)
  }, [])

  // Initialize first step on mount
  useEffect(() => {
    if (initRef.current) return
    initRef.current = true
    const def = stepDefinitions[currentStep as Exclude<StepId, 'thankyou'>]
    if (def) {
      onStepView?.(currentStep)
      void postBotMessages(def.getBotMessages(state, audience))
    }
    // We want this to run exactly once on mount; subsequent step changes
    // are driven by submitStep below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const submitStep = useCallback(async (updates: Partial<LeadState>) => {
    if (isPosting) return
    const newState = { ...state, ...updates }
    setState(newState)

    const currentDef = stepDefinitions[currentStep as Exclude<StepId, 'thankyou'>]
    const userMsg = currentDef?.getUserMessage(newState)

    onStepSubmit?.(currentStep, newState)

    // "Clear-and-replace" pattern (user valgte denne UX over scrolling history).
    // Hvis steppet havde en user-bobble, vises den; ellers ryddes chatten helt
    // (fx welcome med inline Start-CTA hvor der ikke er noget at echo'e).
    setInputVisible(false)
    setMessages(userMsg
      ? [{ type: 'user', text: userMsg, key: `user-${messageKeyRef.current++}` }]
      : []
    )
    await sleep(userMsg ? 600 : 300)

    const next = nextStepId(currentStep, newState, audience)
    if (!next) return

    setCurrentStep(next)

    if (next === 'thankyou') {
      // Send leadet FØRST og vis kun thank-you hvis backend accepterer. Tidligere
      // viste vi thank-you'en med det samme og kaldte onComplete bagefter, så
      // brugere der ramte 4xx (bad phone, dedup, honeypot, network fail) gik
      // glade fra siden i den tro at deres lead var sendt — uden at vi havde
      // nogen registrering af det. Nu rulles steppet tilbage til 'consent' ved
      // fejl så brugeren kan rette og prøve igen, og payloaden ligger i
      // localStorage (submitLead stash'er den) som backup.
      let result: CompleteResult | void = { ok: true }
      try {
        result = await onComplete?.(newState)
      } catch (err) {
        console.error('[Leadflow] onComplete failed', err)
        result = { ok: false, error: err instanceof Error ? err.message : String(err) }
      }
      const accepted = !result || result.ok !== false

      if (accepted) {
        const thanks = [
          `Tusind tak, ${newState.firstName ?? ''}! 🎉`,
          'En forsikringsrådgiver ringer dig op inden for 24 timer 📞',
          'Ofte allerede inden for 1 time i hverdagene mellem 9 og 18 🙂',
        ]
        await postBotMessages(thanks)
        onStepView?.(next)
      } else {
        // Roll tilbage til phone-step (som indeholder phone-input + samtykke-
        // checkbox — der er ikke et separat 'consent' step i flowet) så
        // brugeren kan rette og re-submitte. Vi viser ikke den rå backend-fejl
        // ('HTTP 429: ...') til brugeren — i stedet en venlig besked + et
        // alternativt kontakt-medie så vi ikke taber leadet helt hvis det er
        // en serverside-incident.
        const errors = [
          'Hov! 😬 Der opstod en fejl ved afsendelsen.',
          'Prøv venligst igen om et øjeblik — eller ring til os på 70 60 50 40.',
        ]
        setCurrentStep('phone')
        await postBotMessages(errors)
      }
      return
    }

    const nextDef = stepDefinitions[next as Exclude<StepId, 'thankyou'>]
    if (nextDef) {
      onStepView?.(next)
      await postBotMessages(nextDef.getBotMessages(newState, audience))
    }
  }, [audience, currentStep, isPosting, postBotMessages, state, onComplete, onStepView, onStepSubmit])

  const reset = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
    setState({})
    setMessages([])
    setCurrentStep('welcome')
    setInputVisible(false)
    initRef.current = false
    messageKeyRef.current = 0
    // Re-trigger init effect on next tick
    setTimeout(() => {
      initRef.current = true
      onStepView?.('welcome')
      void postBotMessages(stepDefinitions.welcome.getBotMessages({}, audience))
    }, 50)
  }, [audience, postBotMessages, onStepView])

  const resumeFrom = useCallback((step: StepId, restored: LeadState) => {
    setState(restored)
    setCurrentStep(step)
    setMessages([])
    setInputVisible(false)
    initRef.current = false
    messageKeyRef.current = 0
    setTimeout(() => {
      initRef.current = true
      const def = stepDefinitions[step as Exclude<StepId, 'thankyou'>]
      if (!def) return
      onStepView?.(step)
      void postBotMessages(def.getBotMessages(restored, audience))
    }, 50)
  }, [audience, postBotMessages, onStepView])

  return { state, currentStep, messages, inputVisible, isPosting, submitStep, reset, resumeFrom }
}

/**
 * Ryd den gemte chat-state. Kaldes af Widget når brugeren vælger "Start forfra"
 * (så det forrige flows state ikke lækker ind i det nye) og efter en succesfuld
 * lead-submission (så et fremtidigt besøg ikke åbner en resume-prompt for et
 * allerede afsluttet flow).
 */
export function clearPersistedProgress(): void {
  try { localStorage.removeItem(STORAGE_KEY) } catch { /* ignore quota/disabled */ }
}

export function readPersistedProgress(): { step: StepId; state: LeadState } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LeadState & { _step?: StepId }
    const step = parsed._step
    if (!step || step === 'welcome' || step === 'thankyou') return null
    delete (parsed as { _step?: StepId })._step
    if (Object.keys(parsed).length === 0) return null
    return { step, state: parsed }
  } catch {
    return null
  }
}
